import React, { createContext, useContext, useEffect, useState } from 'react';
import * as api from 'da-pcc-frontend-accountmanagement-api';
import TagManager from 'react-gtm-module';
import withAPI from '../api';
import i18n from '../Localization';
import { usePageError } from './ErrorContext';
import { useLayoutContext } from '../Common/Layout';
import { useTranslation } from 'react-i18next';

type ProfileType = api.UserProfile & { loaded: boolean };

/* Profile context */
const ProfileContext = createContext<ProfileType>(null);

const ProfileContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [ profile, setProfile ] = useState<ProfileType>({ loaded: false });
  const showError               = usePageError();
  const setLayoutInfo           = useLayoutContext();
  const [ m ]                   = useTranslation("marketsegments");

  useEffect(() => {
    withAPI(api.ProfileApi).getProfile().then(
      (profile) => {
        // console.log("%c[  Profile  ]%o", 'color: white; background: black', profile);

        // update Google Analytics dimensions with preferred account info
        const { preferredAccount } = profile;
        const gaUserId = (profile && profile.userId) ? profile.userId : undefined;
        const gaAccountId = (preferredAccount && preferredAccount.id) ? preferredAccount.id : undefined;
        const gaAccountType = (preferredAccount && preferredAccount.type) ? preferredAccount.type : undefined;
        let gaMarketSegment = (preferredAccount && preferredAccount.market) ? preferredAccount.market : undefined;

        /* 
          if account has a market segment defined, use translated market segment name
          for gaMarketSegment value 
        */
        if (gaMarketSegment)  {
          const marketSegments = m("applications");
          gaMarketSegment = marketSegments[preferredAccount.market];
        }

        TagManager.dataLayer({
          dataLayer: {
            userId: gaUserId,
            accountType: gaAccountType,
            accountId: gaAccountId,
            marketSegment: gaMarketSegment,
          }
        });

        // Update language
        if (profile.language !== i18n.language) {
          i18n.changeLanguage(profile.language);
        }

        // Update layout & profile info
        setProfile({ ...profile, loaded: true });
        setLayoutInfo({ firstName: profile.firstName, loggedIn: true });
      },
      err => {
        if (err instanceof Response && err.status !== 401) {
          showError("Server Error");
        }
        else
          throw err;
      }
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ setLayoutInfo, setProfile ]);
  
  const Provider = () => (
    <ProfileContext.Provider value={profile}>
      {children}
    </ProfileContext.Provider>
  );

  return profile.loaded && <Provider />;
}

const useProfileContext = () => useContext(ProfileContext);

export { 
  useProfileContext,
  ProfileContextProvider
};